import { useState, useEffect } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Loader2, Check, X, Plus, Trash2, Send,
  FileText, Image, Video, FileIcon, MapPin, Phone as PhoneIcon, ExternalLink,
  MessageSquare, AlertTriangle, CheckCircle, FolderOpen
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import MediaLibrary from './MediaLibrary';
import { getErrorMessage } from '../../lib/utils';


interface VariableCategory {
  category: { id: string; name: string; display_name: string; icon: string };
  variables: { id: string; variable_key: string; display_name: string; example_value: string }[];
}

interface TemplateButton {
  id: string;
  type: 'quick_reply' | 'url' | 'phone';
  text: string;
  url?: string;
  phone_number?: string;
}

interface CardConfig {
  title?: string;
  subtitle?: string;
  media_url?: string;
  buttons?: TemplateButton[];
}

interface ListPickerConfig {
  button?: string;
  sections?: Array<{
    title: string;
    items: Array<{ id: string; title: string; description?: string }>;
  }>;
}

interface CarouselConfig {
  cards?: Array<{
    title: string;
    body?: string;
    media_url?: string;
    buttons?: TemplateButton[];
  }>;
}

interface AuthConfig {
  add_security_recommendation?: boolean;
  code_expiration_minutes?: number;
}

interface TemplateData {
  friendly_name: string;
  category: 'UTILITY' | 'AUTHENTICATION' | 'MARKETING' | 'SERVICE';
  language: string;
  template_type: 'text' | 'media' | 'quick_reply' | 'call_to_action' | 'card' | 'list_picker' | 'carousel' | 'authentication';
  header_type: 'none' | 'text' | 'image' | 'video' | 'document';
  header_content: string;
  header_media_url: string;
  body_text: string;
  footer_text: string;
  button_type: 'none' | 'quick_reply' | 'call_to_action';
  buttons: TemplateButton[];
  variables: Record<string, string>;
  variable_examples: Record<string, string>;
  card_config?: CardConfig;
  list_picker_config?: ListPickerConfig;
  carousel_config?: CarouselConfig;
  auth_config?: AuthConfig;
}

interface TemplateBuilderProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  template?: Partial<TemplateData> & { id?: string };
}

const STEPS = [
  { id: 1, name: 'Info Base', icon: FileText },
  { id: 2, name: 'Contenuto', icon: MessageSquare },
  { id: 3, name: 'Bottoni', icon: PhoneIcon },
  { id: 4, name: 'Anteprima', icon: CheckCircle },
  { id: 5, name: 'Invia', icon: Send }
];

const LANGUAGES = [
  { code: 'it', name: 'Italiano' },
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' }
];

export default function TemplateBuilder({ open, onClose, onSuccess, template }: TemplateBuilderProps) {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [variableCategories, setVariableCategories] = useState<VariableCategory[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [mediaSource, setMediaSource] = useState<'url' | 'library'>('url');
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);
  const [selectedMediaName, setSelectedMediaName] = useState<string>('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const [data, setData] = useState<TemplateData>({
    friendly_name: '',
    category: 'UTILITY',
    language: 'it',
    template_type: 'text',
    header_type: 'none',
    header_content: '',
    header_media_url: '',
    body_text: '',
    footer_text: '',
    button_type: 'none',
    buttons: [],
    variables: {},
    variable_examples: {},
    card_config: {},
    list_picker_config: {},
    carousel_config: {},
    auth_config: { add_security_recommendation: true, code_expiration_minutes: 10 }
  });

  const loadVariables = async () => {
    try {
      const res = await apiClient.get<VariableCategory[]>('/messaging/variables?scope=contact');
      if (res.success && res.data) {
        setVariableCategories(res.data);
      }
    } catch (err) {
      console.error('Failed to load variables:', err);
    }
  };

  useEffect(() => {
    if (open) {
      loadVariables();
      setExpandedCategories(new Set());
      if (template) {
        setData({
          friendly_name: template.friendly_name || '',
          category: template.category || 'UTILITY',
          language: template.language || 'it',
          template_type: template.template_type || 'text',
          header_type: template.header_type || 'none',
          header_content: template.header_content || '',
          header_media_url: template.header_media_url || '',
          body_text: template.body_text || '',
          footer_text: template.footer_text || '',
          button_type: template.button_type || 'none',
          buttons: template.buttons || [],
          variables: template.variables || {},
          variable_examples: template.variable_examples || {},
          card_config: template.card_config || {},
          list_picker_config: template.list_picker_config || {},
          carousel_config: template.carousel_config || {},
          auth_config: template.auth_config || { add_security_recommendation: true, code_expiration_minutes: 10 }
        });
      } else {
        setData({
          friendly_name: '',
          category: 'UTILITY',
          language: 'it',
          template_type: 'text',
          header_type: 'none',
          header_content: '',
          header_media_url: '',
          body_text: '',
          footer_text: '',
          button_type: 'none',
          buttons: [],
          variables: {},
          variable_examples: {},
          card_config: {},
          list_picker_config: {},
          carousel_config: {},
          auth_config: { add_security_recommendation: true, code_expiration_minutes: 10 }
        });
      }
      setStep(1);
    }
  }, [open, template]);

  const extractVariables = (text: string): string[] => {
    const matches = text.match(/\{\{(\d+)\}\}/g) || [];
    return [...new Set(matches)].sort((a, b) => {
      const numA = parseInt(a.replace(/[{}]/g, ''));
      const numB = parseInt(b.replace(/[{}]/g, ''));
      return numA - numB;
    });
  };

  const validateTemplate = (): string[] => {
    const errors: string[] = [];
    
    if (!data.friendly_name) errors.push('Nome template obbligatorio');
    if (!/^[a-z][a-z0-9_]*$/.test(data.friendly_name)) {
      errors.push('Nome: solo lettere minuscole, numeri e underscore');
    }
    if (!data.body_text) errors.push('Corpo messaggio obbligatorio');
    if (data.body_text.length > 1024) errors.push('Corpo messaggio: max 1024 caratteri');
    if (data.footer_text && data.footer_text.length > 60) errors.push('Footer: max 60 caratteri');
    
    const trimmed = data.body_text.trim();
    if (/^\{\{\d+\}\}/.test(trimmed)) errors.push('Non iniziare con una variabile');
    if (/\{\{\d+\}\}$/.test(trimmed)) errors.push('Non finire con una variabile');
    if (/\{\{\d+\}\}\s*\{\{\d+\}\}/.test(data.body_text)) errors.push('Variabili non adiacenti');
    
    const vars = extractVariables(data.body_text);
    const words = data.body_text.split(/\s+/).filter(w => !/\{\{\d+\}\}/.test(w)).length;
    if (vars.length > 0 && words < (2 * vars.length + 1)) {
      errors.push(`Servono almeno ${2 * vars.length + 1} parole (hai ${words})`);
    }
    
    vars.forEach(v => {
      const num = v.replace(/[{}]/g, '');
      if (!data.variable_examples[num]) {
        errors.push(`Esempio mancante per variabile {{${num}}}`);
      }
    });
    
    return errors;
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const insertVariable = (key: string) => {
    const vars = extractVariables(data.body_text);
    const nextNum = vars.length > 0 
      ? Math.max(...vars.map(v => parseInt(v.replace(/[{}]/g, '')))) + 1 
      : 1;
    setData(prev => ({
      ...prev,
      body_text: prev.body_text + `{{${nextNum}}}`,
      variables: { ...prev.variables, [String(nextNum)]: key }
    }));
  };

  const addButton = () => {
    if (data.button_type === 'quick_reply' && data.buttons.length >= 3) return;
    if (data.button_type === 'call_to_action' && data.buttons.length >= 2) return;
    
    const newButton: TemplateButton = {
      id: Date.now().toString(),
      type: data.button_type === 'quick_reply' ? 'quick_reply' : 'url',
      text: ''
    };
    setData(prev => ({ ...prev, buttons: [...prev.buttons, newButton] }));
  };

  const updateButton = (id: string, updates: Partial<TemplateButton>) => {
    setData(prev => ({
      ...prev,
      buttons: prev.buttons.map(b => b.id === id ? { ...b, ...updates } : b)
    }));
  };

  const removeButton = (id: string) => {
    setData(prev => ({ ...prev, buttons: prev.buttons.filter(b => b.id !== id) }));
  };

  const handleSave = async (submit: boolean = false) => {
    const errors = validateTemplate();
    setValidationErrors(errors);
    if (errors.length > 0) {
      toast({ title: 'Errori di validazione', description: errors[0], variant: 'destructive' });
      return;
    }
    
    // Additional validation for authentication templates
    if (data.template_type === 'authentication') {
      if (data.category !== 'AUTHENTICATION') {
        toast({ title: 'Errore', description: 'I template di autenticazione devono avere categoria AUTHENTICATION', variant: 'destructive' });
        return;
      }
      if (!data.body_text.includes('{{1}}')) {
        toast({ title: 'Errore', description: 'Il template OTP deve contenere {{1}} per il codice di verifica', variant: 'destructive' });
        return;
      }
    }
    
    setSaving(true);
    try {
      // Use PUT for editing existing templates, POST for new ones
      const isEdit = !!template?.id;
      const res = isEdit
        ? await apiClient.put<{ id: string; status: string }>(`/messaging/templates/${template.id}`, data)
        : await apiClient.post<{ id: string; status: string }>('/messaging/templates', data);
      
      if (res.success) {
        const templateId = isEdit ? template.id : res.data?.id;
        if (submit && templateId) {
          setSubmitting(true);
          const submitRes = await apiClient.post(`/messaging/templates/${templateId}/submit`);
          if (submitRes.success) {
            toast({ title: 'Template inviato', description: 'In attesa di approvazione Meta (5min - 24h)' });
          } else {
            toast({ title: 'Errore invio', description: submitRes.error, variant: 'destructive' });
          }
          setSubmitting(false);
        } else {
          toast({ title: isEdit ? 'Template aggiornato' : 'Template salvato', description: isEdit ? 'Modifiche salvate' : 'Salvato come bozza' });
        }
        onSuccess();
        onClose();
      } else {
        toast({ title: 'Errore', description: res.error, variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const renderPreviewMessage = () => {
    let preview = data.body_text;
    Object.entries(data.variable_examples).forEach(([key, value]) => {
      preview = preview.replace(`{{${key}}}`, value || `[var${key}]`);
    });
    return preview;
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <Label>Nome Template *</Label>
              <Input
                value={data.friendly_name}
                onChange={e => setData(prev => ({ ...prev, friendly_name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                placeholder="conferma_prenotazione"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Solo lettere minuscole, numeri e underscore</p>
            </div>
            
            <div>
              <Label>Categoria *</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {([
                  { key: 'MARKETING', emoji: '📢', label: 'Marketing', desc: 'Promozioni, offerte' },
                  { key: 'UTILITY', emoji: '📦', label: 'Utility', desc: 'Conferme, tracking' },
                  { key: 'SERVICE', emoji: '💬', label: 'Service', desc: 'Risposte cliente' },
                  { key: 'AUTHENTICATION', emoji: '🔐', label: 'Authentication', desc: 'Codici OTP' }
                ] as const).map(cat => (
                  <Card
                    key={cat.key}
                    className={`cursor-pointer transition-all ${data.category === cat.key ? 'ring-2 ring-primary bg-primary/5' : ''}`}
                    onClick={() => setData(prev => ({ 
                      ...prev, 
                      category: cat.key,
                      template_type: cat.key === 'AUTHENTICATION' ? 'authentication' : 'text'
                    }))}
                  >
                    <CardContent className="p-3 text-center">
                      <div className="text-2xl mb-1">{cat.emoji}</div>
                      <div className="font-medium text-sm">{cat.label}</div>
                      <div className="text-xs text-muted-foreground">{cat.desc}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            
            {/* Config OTP inline per Authentication */}
            {data.category === 'AUTHENTICATION' && (
              <div className="border rounded-lg p-4 space-y-3 bg-amber-50 border-amber-200">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🔐</span>
                  <Label className="text-sm font-semibold text-amber-900">Configurazione OTP</Label>
                </div>
                <p className="text-xs text-amber-700">
                  Il body del template deve contenere <code className="bg-amber-100 px-1 rounded">{'{{1}}'}</code> per il codice di verifica
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="addSecurityDisclaimer"
                      checked={data.auth_config?.add_security_recommendation !== false}
                      onChange={e => setData(prev => ({
                        ...prev,
                        auth_config: { ...prev.auth_config, add_security_recommendation: e.target.checked }
                      }))}
                      className="rounded"
                    />
                    <Label htmlFor="addSecurityDisclaimer" className="text-xs text-amber-800">
                      Aggiungi disclaimer sicurezza
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-amber-800">Validità (min):</Label>
                    <Input
                      type="number"
                      value={data.auth_config?.code_expiration_minutes || 10}
                      onChange={e => setData(prev => ({
                        ...prev,
                        auth_config: { ...prev.auth_config, code_expiration_minutes: parseInt(e.target.value) || 10 }
                      }))}
                      min={1}
                      max={60}
                      className="w-16 h-8"
                    />
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <Label>Lingua *</Label>
              <Select value={data.language} onValueChange={v => setData(prev => ({ ...prev, language: v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(lang => (
                    <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
          </div>
        );

      case 2:
        return (
          <div className="grid grid-cols-5 gap-4">
            <div className="col-span-3 space-y-4">
              <div>
                <Label>Header (opzionale)</Label>
                <Select value={data.header_type} onValueChange={(v: string) => setData(prev => ({ ...prev, header_type: v as TemplateData['header_type'] }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno</SelectItem>
                    <SelectItem value="text">Testo</SelectItem>
                    <SelectItem value="image">Immagine</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="document">Documento</SelectItem>
                  </SelectContent>
                </Select>
                {data.header_type === 'text' && (
                  <Input
                    value={data.header_content}
                    onChange={e => setData(prev => ({ ...prev, header_content: e.target.value.slice(0, 60) }))}
                    placeholder="Titolo header"
                    className="mt-2"
                    maxLength={60}
                  />
                )}
                {(data.header_type === 'image' || data.header_type === 'video' || data.header_type === 'document') && (
                  <div className="mt-2 space-y-3">
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="mediaSource" 
                          checked={mediaSource === 'url'} 
                          onChange={() => setMediaSource('url')}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">URL manuale</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="mediaSource" 
                          checked={mediaSource === 'library'} 
                          onChange={() => setMediaSource('library')}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Libreria Media</span>
                      </label>
                    </div>
                    
                    {mediaSource === 'url' ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          {data.header_type === 'image' && <Image className="h-4 w-4 text-muted-foreground" />}
                          {data.header_type === 'video' && <Video className="h-4 w-4 text-muted-foreground" />}
                          {data.header_type === 'document' && <FileIcon className="h-4 w-4 text-muted-foreground" />}
                          <span className="text-sm text-muted-foreground">
                            {data.header_type === 'image' ? 'URL immagine (JPG, PNG - max 5MB)' :
                             data.header_type === 'video' ? 'URL video (MP4 - max 16MB)' :
                             'URL documento (PDF - max 100MB)'}
                          </span>
                        </div>
                        <Input
                          value={data.header_media_url}
                          onChange={e => {
                            setData(prev => ({ ...prev, header_media_url: e.target.value }));
                            setSelectedMediaName('');
                          }}
                          placeholder="https://esempio.com/media/file.jpg"
                          type="url"
                        />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setMediaLibraryOpen(true)}
                          className="w-full justify-start"
                        >
                          <FolderOpen className="h-4 w-4 mr-2" />
                          {selectedMediaName || 'Seleziona dalla libreria...'}
                        </Button>
                        {data.header_media_url && (
                          <p className="text-xs text-muted-foreground truncate">
                            URL: {data.header_media_url}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {data.header_media_url && data.header_type === 'image' && (
                      <div className="mt-2 rounded-lg overflow-hidden border max-w-[200px]">
                        <img 
                          src={data.header_media_url} 
                          alt="Header preview" 
                          className="w-full h-auto"
                          onError={(e) => (e.target as HTMLImageElement).style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>
                )}
                
                <Dialog open={mediaLibraryOpen} onOpenChange={setMediaLibraryOpen}>
                  <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
                    <DialogHeader>
                      <DialogTitle>Seleziona Media</DialogTitle>
                      <DialogDescription>
                        Scegli un file dalla libreria media
                      </DialogDescription>
                    </DialogHeader>
                    <div className="overflow-y-auto max-h-[60vh]">
                      <MediaLibrary 
                        selectionMode={true}
                        onSelect={(media) => {
                          setData(prev => ({ ...prev, header_media_url: media.media_url }));
                          setSelectedMediaName(media.friendly_name);
                          setMediaLibraryOpen(false);
                        }}
                      />
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              
              <div>
                <div className="flex items-center justify-between">
                  <Label>Corpo Messaggio *</Label>
                  <span className="text-xs text-muted-foreground">{data.body_text.length}/1024</span>
                </div>
                <div className="flex gap-1 mt-1 mb-2">
                  <Button size="sm" variant="outline" onClick={() => setData(prev => ({ ...prev, body_text: prev.body_text + '*testo*' }))}>
                    <b>B</b>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setData(prev => ({ ...prev, body_text: prev.body_text + '_testo_' }))}>
                    <i>I</i>
                  </Button>
                </div>
                <Textarea
                  value={data.body_text}
                  onChange={e => setData(prev => ({ ...prev, body_text: e.target.value.slice(0, 1024) }))}
                  placeholder="Ciao {{1}}, la tua prenotazione è confermata..."
                  rows={6}
                  className="font-mono"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between">
                  <Label>Footer (opzionale)</Label>
                  <span className="text-xs text-muted-foreground">{data.footer_text.length}/60</span>
                </div>
                <Input
                  value={data.footer_text}
                  onChange={e => setData(prev => ({ ...prev, footer_text: e.target.value.slice(0, 60) }))}
                  placeholder="Rispondi STOP per cancellare"
                  className="mt-1"
                  maxLength={60}
                />
              </div>
              
              {/* Card Configuration */}
              {data.template_type === 'card' && (
                <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🃏</span>
                    <Label className="text-sm font-semibold">Configurazione Card</Label>
                  </div>
                  <div>
                    <Label className="text-xs">Titolo Card</Label>
                    <Input
                      value={data.card_config?.title || ''}
                      onChange={e => setData(prev => ({
                        ...prev,
                        card_config: { ...prev.card_config, title: e.target.value }
                      }))}
                      placeholder="Nome prodotto o servizio"
                      className="mt-1"
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Sottotitolo</Label>
                    <Input
                      value={data.card_config?.subtitle || ''}
                      onChange={e => setData(prev => ({
                        ...prev,
                        card_config: { ...prev.card_config, subtitle: e.target.value }
                      }))}
                      placeholder="Descrizione breve"
                      className="mt-1"
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">URL Immagine Card</Label>
                    <Input
                      value={data.card_config?.media_url || ''}
                      onChange={e => setData(prev => ({
                        ...prev,
                        card_config: { ...prev.card_config, media_url: e.target.value }
                      }))}
                      placeholder="https://..."
                      type="url"
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
              
              {/* List Picker Configuration */}
              {data.template_type === 'list_picker' && (
                <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📋</span>
                    <Label className="text-sm font-semibold">Configurazione Lista</Label>
                  </div>
                  <div>
                    <Label className="text-xs">Titolo Pulsante Menu</Label>
                    <Input
                      value={data.list_picker_config?.button || ''}
                      onChange={e => setData(prev => ({
                        ...prev,
                        list_picker_config: { ...prev.list_picker_config, button: e.target.value, sections: prev.list_picker_config?.sections || [] }
                      }))}
                      placeholder="Vedi opzioni"
                      className="mt-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    La lista apparirà come menu espandibile con le opzioni configurate.
                    Supporta fino a 10 opzioni in una o più sezioni.
                  </p>
                </div>
              )}
              
              
              {/* Carousel Notice */}
              {data.template_type === 'carousel' && (
                <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🎠</span>
                    <Label className="text-sm font-semibold">Carousel (2-10 Card)</Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    I Carousel permettono di mostrare più prodotti/servizi in un formato scorrevole.
                    Configura ogni card con titolo, immagine e pulsanti CTA.
                  </p>
                  <div className="text-xs">
                    <Badge variant="outline">Funzionalità avanzata - disponibile prossimamente</Badge>
                  </div>
                </div>
              )}
            </div>
            
            <div className="col-span-2">
              <Label>Variabili Disponibili</Label>
              <ScrollArea className="h-[350px] mt-2 border rounded-md p-1">
                {variableCategories.map(cat => {
                  const isExpanded = expandedCategories.has(cat.category.id);
                  return (
                    <div key={cat.category.id} className="mb-1">
                      <button
                        type="button"
                        className="w-full flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-muted/50 transition-colors text-left"
                        onClick={() => toggleCategory(cat.category.id)}
                      >
                        <span className="font-medium text-sm">{cat.category.display_name}</span>
                        <div className="flex items-center gap-1">
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">{cat.variables.length}</Badge>
                          {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="pl-1 pb-1 space-y-0.5">
                          {cat.variables.map(v => (
                            <Button
                              key={v.id}
                              size="sm"
                              variant="ghost"
                              className="w-full justify-start text-xs h-7 hover:scale-100 active:scale-100"
                              onClick={() => insertVariable(v.variable_key)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              {v.display_name}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </ScrollArea>
              
              {extractVariables(data.body_text).length > 0 && (
                <div className="mt-4">
                  <Label>Esempi Variabili</Label>
                  <div className="space-y-2 mt-2">
                    {extractVariables(data.body_text).map(v => {
                      const num = v.replace(/[{}]/g, '');
                      return (
                        <div key={num} className="flex gap-2 items-center">
                          <Badge variant="outline">{`{{${num}}}`}</Badge>
                          <Input
                            value={data.variable_examples[num] || ''}
                            onChange={e => setData(prev => ({
                              ...prev,
                              variable_examples: { ...prev.variable_examples, [num]: e.target.value }
                            }))}
                            placeholder="Valore esempio"
                            className="flex-1 h-8 text-sm"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div>
              <Label>Tipo Bottoni</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[
                  { value: 'none', label: 'Nessuno', icon: X },
                  { value: 'quick_reply', label: 'Quick Reply', icon: MessageSquare },
                  { value: 'call_to_action', label: 'Call-to-Action', icon: ExternalLink }
                ].map(opt => (
                  <Card
                    key={opt.value}
                    className={`cursor-pointer transition-all ${data.button_type === opt.value ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setData(prev => ({ ...prev, button_type: opt.value as TemplateData['button_type'], buttons: [] }))}
                  >
                    <CardContent className="p-3 text-center">
                      <opt.icon className="h-5 w-5 mx-auto mb-1" />
                      <div className="text-sm font-medium">{opt.label}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            
            {data.button_type !== 'none' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>
                    Bottoni ({data.buttons.length}/{data.button_type === 'quick_reply' ? 3 : 2})
                  </Label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={addButton}
                    disabled={
                      (data.button_type === 'quick_reply' && data.buttons.length >= 3) ||
                      (data.button_type === 'call_to_action' && data.buttons.length >= 2)
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" /> Aggiungi
                  </Button>
                </div>
                
                {data.buttons.map((btn, idx) => (
                  <Card key={btn.id} className="p-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 space-y-2">
                        {data.button_type === 'call_to_action' && (
                          <Select
                            value={btn.type}
                            onValueChange={v => updateButton(btn.id, { type: v as TemplateButton['type'] })}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="url">URL</SelectItem>
                              <SelectItem value="phone">Telefono</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        <Input
                          value={btn.text}
                          onChange={e => updateButton(btn.id, { text: e.target.value })}
                          placeholder="Testo bottone"
                        />
                        {btn.type === 'url' && (
                          <Input
                            value={btn.url || ''}
                            onChange={e => updateButton(btn.id, { url: e.target.value })}
                            placeholder="https://esempio.com/{{1}}"
                          />
                        )}
                        {btn.type === 'phone' && (
                          <Input
                            value={btn.phone_number || ''}
                            onChange={e => updateButton(btn.id, { phone_number: e.target.value })}
                            placeholder="+39 02 1234567"
                          />
                        )}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeButton(btn.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 4:
        const errors = validateTemplate();
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-2 block">Anteprima WhatsApp</Label>
              <div className="bg-[#e5ddd5] p-4 rounded-lg min-h-[300px]">
                <div className="bg-white rounded-lg shadow max-w-[280px] ml-auto">
                  {data.header_type === 'text' && data.header_content && (
                    <div className="px-3 pt-2 font-medium text-sm border-b pb-2">
                      {data.header_content}
                    </div>
                  )}
                  {['image', 'video', 'document'].includes(data.header_type) && (
                    <div className="h-32 bg-gray-200 rounded-t-lg flex items-center justify-center overflow-hidden">
                      {data.header_type === 'image' && data.header_media_url ? (
                        <img 
                          src={data.header_media_url} 
                          alt="Header" 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            (e.target as HTMLImageElement).parentElement?.classList.add('flex', 'items-center', 'justify-center');
                          }}
                        />
                      ) : data.header_type === 'image' ? (
                        <Image className="h-8 w-8 text-gray-400" />
                      ) : data.header_type === 'video' ? (
                        <Video className="h-8 w-8 text-gray-400" />
                      ) : (
                        <FileIcon className="h-8 w-8 text-gray-400" />
                      )}
                    </div>
                  )}
                  <div className="p-3">
                    <div className="text-sm whitespace-pre-wrap">{renderPreviewMessage()}</div>
                    {data.footer_text && (
                      <div className="text-xs text-gray-500 mt-2 pt-2 border-t">
                        {data.footer_text}
                      </div>
                    )}
                    <div className="text-right text-xs text-gray-400 mt-1">
                      {new Date().toLocaleTimeString('it', { hour: '2-digit', minute: '2-digit' })} ✓
                    </div>
                  </div>
                  {data.buttons.length > 0 && (
                    <div className="border-t">
                      {data.buttons.map(btn => (
                        <div
                          key={btn.id}
                          className="text-center text-blue-500 py-2 border-b last:border-0 text-sm font-medium"
                        >
                          {btn.type === 'url' && <ExternalLink className="h-3 w-3 inline mr-1" />}
                          {btn.type === 'phone' && <PhoneIcon className="h-3 w-3 inline mr-1" />}
                          {btn.text || 'Bottone'}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">Validazione</Label>
              <div className="space-y-2">
                {errors.length === 0 ? (
                  <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                    <CheckCircle className="h-5 w-5" />
                    <span>Tutti i controlli superati</span>
                  </div>
                ) : (
                  errors.map((err, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
                      <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                      <span className="text-sm">{err}</span>
                    </div>
                  ))
                )}
              </div>
              
              <div className="mt-4 space-y-2">
                <div className="text-sm"><strong>Nome:</strong> {data.friendly_name}</div>
                <div className="text-sm"><strong>Categoria:</strong> {data.category}</div>
                <div className="text-sm"><strong>Lingua:</strong> {LANGUAGES.find(l => l.code === data.language)?.name}</div>
                <div className="text-sm"><strong>Variabili:</strong> {extractVariables(data.body_text).length}</div>
                <div className="text-sm"><strong>Bottoni:</strong> {data.buttons.length}</div>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">📤</div>
            <h3 className="text-xl font-semibold mb-2">Pronto per l'invio</h3>
            <p className="text-muted-foreground mb-6">
              Il template verrà inviato a Meta per l'approvazione.
              <br />
              Il processo richiede da 5 minuti a 24 ore.
            </p>
            
            <div className="bg-muted p-4 rounded-lg max-w-md mx-auto mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><strong>Nome:</strong> {data.friendly_name}</div>
                <div><strong>Categoria:</strong> {data.category}</div>
                <div><strong>Lingua:</strong> {data.language}</div>
                <div><strong>Variabili:</strong> {extractVariables(data.body_text).length}</div>
              </div>
            </div>
            
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={() => handleSave(false)} disabled={saving}>
                {saving && !submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salva Bozza
              </Button>
              <Button onClick={() => handleSave(true)} disabled={saving || submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Send className="h-4 w-4 mr-2" />
                Invia per Approvazione
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            {template ? 'Modifica Template' : 'Nuovo Template WhatsApp'}
          </DialogTitle>
          <DialogDescription>
            Crea un template per messaggi WhatsApp che richiedono approvazione Meta
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex items-center justify-between mb-4 border-b pb-4">
          {STEPS.map((s, idx) => (
            <div
              key={s.id}
              className={`flex items-center gap-2 cursor-pointer ${
                step === s.id ? 'text-primary' : step > s.id ? 'text-green-600' : 'text-muted-foreground'
              }`}
              onClick={() => setStep(s.id)}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === s.id ? 'bg-primary text-primary-foreground' : step > s.id ? 'bg-green-100' : 'bg-muted'
              }`}>
                {step > s.id ? <Check className="h-4 w-4" /> : s.id}
              </div>
              <span className="text-sm font-medium hidden sm:inline">{s.name}</span>
              {idx < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            </div>
          ))}
        </div>
        
        {renderStep()}
        
        {step < 5 && (
          <div className="flex justify-between mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Indietro
            </Button>
            <Button onClick={() => setStep(s => Math.min(5, s + 1))}>
              Avanti <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}